<template>
  <div :class="bem()">
    <button
      v-for="(item, index) in slideCountForMobileIndicators"
      :key="index"
      :class="
        bem('thumb-tab', {
          mobile: true,
          selected: activeSlideIndex === index,
        })
      "
      @click="emits('click', index)"
    />
    <button
      v-for="(viewAngle, viewAngleIndex) in staticViewAngles"
      :key="viewAngleIndex"
      :class="
        bem('thumb-tab', {
          selected: activeSlideIndex === viewAngleIndex,
          desktop: true,
        })
      "
      @click="emits('click', viewAngleIndex)"
    >
      <div :class="bem('image')">
        <StaticThreeSixtyFrame
          v-if="show360"
          :key="`static-image-360-${viewAngle}`"
          :scale="customImageScaling[viewAngle]"
          :customised-product="customisedProduct"
          :show-iris-preview="showIrisPreview"
          :frame-number="getFrameNumber(viewAngle)"
          :show-secondary="viewAngle === 5"
          :unpadded="true"
          :show-test-images="showRenderUpliftTest"
          :class="bem('360-image')"
        />

        <ProductImg
          v-else
          :key="`static-image-product-${viewAngle}`"
          :view-angle="viewAngle"
          :customised-product="customisedProduct"
          :show-iris-preview="showIrisPreview"
        />
      </div>
    </button>
    <button
      v-for="(
        lifestyleImage, lifestyleImageIndex
      ) in staticProductAndLifestyleImages"
      :key="lifestyleImageIndex"
      :class="
        bem('thumb-tab', {
          selected:
            activeSlideIndex === lifestyleImageIndex + staticViewAngles.length,
          desktop: true,
        })
      "
      @click="emits('click', lifestyleImageIndex + staticViewAngles.length)"
    >
      <DatoCmsImage
        v-if="lifestyleImage?.responsiveImage?.srcSet"
        :key="lifestyleImage.responsiveImage.src"
        :data="lifestyleImage.responsiveImage"
        object-fit="cover"
        :object-position="`${lifestyleImage.focalPoint.x * 100}% ${
          lifestyleImage.focalPoint.y * 100
        }%`"
        :class="bem('image', { lifestyle: true })"
        sizes="200px"
        :priority="true"
      />
    </button>
    <button
      v-if="showLensGuideButton"
      :class="bem('thumb-tab', { lensGuide: true, desktop: true })"
      @click="$emit('show-lens-guide', true)"
    >
      <VisionIcon
        id="product-gallery-thumbs-scene"
        :class="bem('lens-guide-icon')"
        icon="scene"
        gradient="default"
        size="small"
      />
      <span :class="bem('lens-guide-text')">
        Lens <br />
        Guide
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'

const { $abt } = useNuxtApp()
const showRenderUpliftTest = $abt.variant('renderuplift') === 'variant'

interface ProductGalleryThumbsProps {
  activeSlideIndex: number
  staticViewAngles?: Array<number>
  customImageScaling?: object
  customisedProduct: any
  staticProductAndLifestyleImages?: Array<object>
  showIrisPreview: boolean
  show360: boolean
  viewAngleMapping: object
  showLensGuideButton: boolean
}

const props = withDefaults(defineProps<ProductGalleryThumbsProps>(), {
  staticViewAngles: () => [],
  customImageScaling: () => ({}),
  staticProductAndLifestyleImages: () => [],
  showIrisPreview: false,
  show360: false,
  viewAngleMapping: () => ({}),
  showLensGuideButton: false,
})

const emits = defineEmits(['click', 'show-lens-guide'])

const bem = useBEM('ProductGalleryThumbs')

const slideCountForMobileIndicators = [
  ...props.staticViewAngles,
  ...props.staticProductAndLifestyleImages,
].length

function getFrameNumber(viewAngle) {
  return props.viewAngleMapping[viewAngle] || 1
}
</script>

<style lang="postcss" scoped>
.ProductGalleryThumbs {
  @apply flex gap-x-2 md:gap-x-1;

  @screen lg {
    @apply flex-col gap-y-2;
  }
}

.ProductGalleryThumbs__image {
  @apply aspect-square px-1.5;
}

.ProductGalleryThumbs__360-image {
  @apply relative;
}

.ProductGalleryThumbs__image--lifestyle {
  @apply w-full;
}

.ProductGalleryThumbs__thumb-tab {
  @apply aspect-square w-9 border border-grey-300 rounded-md overflow-hidden smMax:bg-grey-lighter;

  @screen xxs {
    @apply w-10;
  }

  @screen xs {
    @apply w-12;
  }

  @screen sm {
    @apply w-16;
  }

  @screen md {
    @apply w-full;
  }

  &:hover {
    @apply border-grey-500;
  }

  &--desktop {
    @apply hidden md:block;
  }

  &--mobile {
    @apply block md:hidden
      rounded-full
      bg-grey-400
      border-none
      w-2 sm:w-3 h-2 sm:h-3;
  }
}

.ProductGalleryThumbs__thumb-tab--mobile.ProductGalleryThumbs__thumb-tab--selected {
  @apply bg-black;
}

.ProductGalleryThumbs__thumb-tab--selected {
  @apply border-black bg-white;
}

.ProductGalleryThumbs__thumb-tab--lens-guide {
  @apply hidden md:flex items-center justify-center flex-col;
}

.ProductGalleryThumbs__lens-guide-icon {
  @apply hidden lg:block;
}

.ProductGalleryThumbs__lens-guide-text {
  @apply block t-gradient text-center smMax:text-tiny smMax:leading-3 text-xxs font-bold leading-4;
}

/* Dark Chrome Theme */
.is-dark-chrome-theme .ProductGalleryThumbs {
  --vision-slido-bg-colour: theme('colors.grey-dark');
  --vision-slido-border-colour: theme('colors.grey.600');
  --vision-slido-tab-active-border-color: theme('colors.grey.600');
  --vision-slido-tab-active-background-color: theme('colors.black');

  @screen mdMax {
    --vision-slido-bg-colour: theme('colors.grey.800');
    --vision-slido-border-colour: theme('colors.grey.600');
  }
}
</style>
